import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback banners if none from API
const defaultBanners = [
  {
    banner_id: 'default_1',
    title: 'Diventa Partner di Moneyfor',
    description: 'Promuovi la tua azienda supportando chi ha bisogno',
    image_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=200&fit=crop',
    link_url: '/corporate-fund',
    bgColor: 'from-emerald-600 to-emerald-800'
  },
  {
    banner_id: 'default_2',
    title: 'Responsabilità Sociale d\'Impresa',
    description: 'Fai crescere il tuo brand aiutando la comunità',
    image_url: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=200&fit=crop',
    link_url: '/corporate-fund',
    bgColor: 'from-indigo-600 to-indigo-800'
  },
  {
    banner_id: 'default_3',
    title: 'Il tuo spazio pubblicitario qui',
    description: 'Raggiungi migliaia di utenti solidali',
    image_url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=200&fit=crop',
    link_url: '/advertise',
    bgColor: 'from-amber-500 to-amber-700'
  }
];

const gradients = [
  'from-emerald-600 to-emerald-800',
  'from-indigo-600 to-indigo-800',
  'from-amber-500 to-amber-700',
  'from-rose-500 to-rose-700',
  'from-violet-600 to-violet-800',
];

// Hook to fetch banners from API
const useBanners = () => {
  const [banners, setBanners] = useState(defaultBanners);
  
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/banners`);
        if (response.data && response.data.length > 0) {
          setBanners(response.data.map((b, i) => ({
            ...b,
            bgColor: b.bgColor || gradients[i % gradients.length]
          })));
        }
      } catch { /* use defaults */ }
    };
    fetchBanners();
  }, []);

  return banners;
};

const isExternalLink = (url) => {
  return url && (url.startsWith('http') || url.startsWith('mailto'));
};

// Banner orizzontale grande (per header/hero)
export const BannerAdLarge = ({ index = 0, language = 'it' }) => {
  const banners = useBanners();
  const ad = banners[index % banners.length];
  const external = isExternalLink(ad.link_url);
  
  return (
    <a 
      href={ad.link_url}
      target={external ? '_blank' : '_self'}
      rel={external ? 'noopener noreferrer' : undefined}
      className="block"
      data-testid={`banner-large-${ad.banner_id}`}
    >
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${ad.bgColor || gradients[0]} group`}>
        <div className="absolute inset-0 opacity-20">
          {ad.image_url && <img src={ad.image_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="relative p-6 md:p-8 flex items-center justify-between">
          <div className="text-white">
            <p className="text-xs uppercase tracking-wider text-white/70 mb-1">
              {language === 'it' ? 'Sponsorizzato' : 'Sponsored'}
            </p>
            <h3 className="font-heading text-xl md:text-2xl font-bold mb-2">{ad.title}</h3>
            <p className="text-white/80 text-sm md:text-base">{ad.description}</p>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white font-medium group-hover:bg-white/30 transition-colors">
            {language === 'it' ? 'Scopri di più' : 'Learn more'}
            <ExternalLink className="h-4 w-4" />
          </div>
        </div>
      </div>
    </a>
  );
};

// Banner compatto per sidebar o inline
export const BannerAdCompact = ({ index = 0, language = 'it' }) => {
  const banners = useBanners();
  const ad = banners[index % banners.length];
  const external = isExternalLink(ad.link_url);
  
  return (
    <a 
      href={ad.link_url}
      target={external ? '_blank' : '_self'}
      rel={external ? 'noopener noreferrer' : undefined}
      className="block"
      data-testid={`banner-compact-${ad.banner_id}`}
    >
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-md transition-shadow group">
        <div className="h-24 relative">
          {ad.image_url && <img src={ad.image_url} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider text-white/80 bg-black/30 px-2 py-0.5 rounded">
            {language === 'it' ? 'Annuncio' : 'Ad'}
          </span>
        </div>
        <div className="p-4">
          <h4 className="font-semibold text-stone-900 text-sm mb-1 group-hover:text-emerald-700 transition-colors">{ad.title}</h4>
          <p className="text-xs text-stone-500 line-clamp-2">{ad.description}</p>
        </div>
      </div>
    </a>
  );
};

// Banner inline nel feed (tra i post)
export const BannerAdInline = ({ index = 0, language = 'it' }) => {
  const banners = useBanners();
  const ad = banners[index % banners.length];
  const external = isExternalLink(ad.link_url);
  
  return (
    <a 
      href={ad.link_url}
      target={external ? '_blank' : '_self'}
      rel={external ? 'noopener noreferrer' : undefined}
      className="block"
      data-testid={`banner-inline-${ad.banner_id}`}
    >
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${ad.bgColor || gradients[0]} group`}>
        <div className="absolute top-2 right-2 z-10">
          <span className="text-[10px] uppercase tracking-wider text-white/70 bg-black/20 px-2 py-1 rounded-full">
            {language === 'it' ? 'Sponsorizzato' : 'Sponsored'}
          </span>
        </div>
        <div className="flex items-center">
          <div className="w-1/3 h-32">
            {ad.image_url && <img src={ad.image_url} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 p-5 text-white">
            <h4 className="font-heading text-lg font-bold mb-1">{ad.title}</h4>
            <p className="text-white/80 text-sm mb-3">{ad.description}</p>
            <span className="inline-flex items-center gap-1 text-sm font-medium bg-white/20 px-3 py-1 rounded-full group-hover:bg-white/30 transition-colors">
              {language === 'it' ? 'Scopri' : 'Discover'}
              <ExternalLink className="h-3 w-3" />
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

export default BannerAdLarge;
